import PageHero from '@/components/feature/PageHero';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { outreachHero, outreachStats, initiatives, outreachGallery, outreachTestimonials } from '@/mocks/outreach';

export default function OutreachPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <PageHero
        title={outreachHero.title}
        subtitle={outreachHero.subtitle}
        imageUrl={outreachHero.imageUrl}
        breadcrumb={{ label: 'Home', href: '/' }}
      />

      {/* Stats */}
      <section className="py-8 md:py-10 bg-background-50 border-b border-background-200/70">
        <div className="px-4 md:px-6 max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {outreachStats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl md:text-4xl font-heading font-bold text-secondary-500 mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-foreground-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Initiatives */}
      <section className="py-14 md:py-18 bg-background-50">
        <div className="px-4 md:px-6 max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-4">
            Our Initiatives
          </h2>
          <p className="text-foreground-600 text-center max-w-2xl mx-auto mb-12">
            We run multiple programs designed to reach underserved communities through different approaches.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {initiatives.map((initiative) => (
              <div key={initiative.id} className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden group transition-all duration-300 hover:border-secondary-300/50">
                <div className="flex flex-col sm:flex-row">
                  <div className="w-full sm:w-56 h-48 sm:h-auto flex-shrink-0 overflow-hidden">
                    <img
                      src={initiative.imageUrl}
                      alt={initiative.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5 flex-1">
                    <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center mb-3">
                      <i className={`${initiative.icon} text-lg text-secondary-600 w-5 h-5 flex items-center justify-center`}></i>
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-foreground-950 mb-2">
                      {initiative.title}
                    </h3>
                    <p className="text-foreground-600 text-sm leading-relaxed mb-3">
                      {initiative.description}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-secondary-600 font-medium">
                      <i className="ri-bar-chart-line text-sm w-4 h-4 flex items-center justify-center"></i>
                      <span>{initiative.impact}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-14 md:py-18 bg-background-100">
        <div className="px-4 md:px-6 max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-4">
            Outreach in Action
          </h2>
          <p className="text-foreground-600 text-center max-w-2xl mx-auto mb-12">
            See the impact we are making across Pulau Pinang communities.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {outreachGallery.map((item, i) => (
              <div key={i} className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden group transition-all duration-300 hover:border-secondary-300/50">
                <div className="h-56 overflow-hidden">
                  <img
                    src={item.imageUrl}
                    alt={item.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-4">
                  <h4 className="font-heading font-semibold text-foreground-950 mb-1">{item.title}</h4>
                  <p className="text-foreground-600 text-sm">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-14 md:py-18 bg-background-50">
        <div className="px-4 md:px-6 max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-12">
            Voices from the Community
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {outreachTestimonials.map((t, i) => (
              <div key={i} className="bg-background-100 border border-background-200/70 rounded-lg p-6">
                <i className="ri-double-quotes-l text-3xl text-secondary-300 mb-3 block w-8 h-8 flex items-center justify-center"></i>
                <p className="text-foreground-700 text-sm leading-relaxed mb-4 italic">
                  &ldquo;{t.text}&rdquo;
                </p>
                <div>
                  <p className="font-medium text-foreground-950 text-sm">{t.name}</p>
                  <p className="text-foreground-500 text-xs">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-16 bg-secondary-500">
        <div className="px-4 md:px-6 max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">
            Help Us Reach Further
          </h2>
          <p className="text-background-50/80 text-base mb-8 leading-relaxed">
            Volunteer your time, donate equipment, or sponsor an outreach event. Every contribution extends our reach.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="mailto:insanperwirapenang@gmail.com"
              className="px-6 py-3 rounded-full bg-background-50 text-secondary-700 font-medium text-sm hover:bg-background-100 transition-all whitespace-nowrap"
            >
              Volunteer With Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}